import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiError, getAppSettings } from "@/lib/api";
import { publicAppSettingsKey, unscopedKey } from "@/lib/queryClient";
import { useScoreRevealStore, useScoreRevealed } from "@/stores/scoreReveal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useAuthStore } from "@/stores/auth";
import { hasAdminAccess } from "@/features/admin/nav";
import { slotKeyToUrl } from "@/features/game/pages/GameShell";
import {
  firstReadySlot,
  hasRecordedScore,
  matchHasReadyVideo,
  processingProgress,
} from "@/features/season/seasonHelpers";
import { getSideLogos } from "@/lib/matchLogos";
import type { Match } from "@/lib/types";

/**
 * Public match grid.
 *
 * Extracted from `SeasonShell` (M11a) in M12a so the team page
 * (`/teams/$teamSlug`) can render the same card layout without
 * duplicating MatchCard / per-card affordances. The grid itself is
 * presentational; callers own the filter/search/header chrome that
 * decides which matches to pass in.
 *
 * Cache invalidation contract:
 *   - The per-card Delete action invalidates BOTH the global public
 *     matches key AND the per-team-slug key (when provided). This
 *     keeps the season view + team page coherent after a deletion
 *     without the caller wiring up its own onDelete hook.
 */
export interface MatchGridProps {
  matches: Match[];
  /** Optional team slug — when set, the per-card Delete action also
   * invalidates the team-scoped matches query so the row disappears
   * from the team page on success. */
  teamSlug?: string;
  /** Empty-state copy. Pass null/undefined to render nothing on empty. */
  emptyMessage?: string;
  testid?: string;
}

export function MatchGrid({
  matches,
  teamSlug,
  emptyMessage = "No matches yet.",
  testid = "match-grid",
}: MatchGridProps) {
  // The tenant club's logo is one setting per install; pull it once at
  // the grid level and pass it to each card so the tenant side always
  // renders the same mark and the per-match opponent_logo only drives
  // the OTHER side.
  const settingsQuery = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });
  const clubLogoUrl = settingsQuery.data?.assets?.club_logo_url ?? null;
  if (matches.length === 0) {
    return (
      <p
        data-testid={`${testid}-empty`}
        className="text-ink-muted text-sm font-mono"
      >
        {emptyMessage}
      </p>
    );
  }
  return (
    <ul
      data-testid={testid}
      role="list"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
    >
      {matches.map((match) => (
        <li key={match.id}>
          <MatchCard match={match} teamSlug={teamSlug} clubLogoUrl={clubLogoUrl} />
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function MatchCard({
  match,
  teamSlug,
  clubLogoUrl,
}: {
  match: Match;
  teamSlug?: string;
  clubLogoUrl: string | null;
}) {
  const { homeUrl, awayUrl } = getSideLogos(match, clubLogoUrl);
  const reveal = useScoreRevealStore((s) => s.reveal);
  const revealed = useScoreRevealed(match.id);
  const authMe = useAuthStore((s) => s.me);
  // Edit pencil (→ /admin/matches) shows for whoever can reach the admin
  // console: `hasAdminAccess` (global admin / club_admin / team_admin). The
  // legacy `uploader` token that used to gate it was retired in deprecation
  // PR 3 (its writers are now team_admin via migration 0045). Delete trash
  // stays admin-only.
  const roles = authMe?.user?.roles ?? [];
  const canEdit = hasAdminAccess(authMe);
  const canDelete = roles.includes("admin");

  const scoreHidden = hasRecordedScore(match) && !revealed;
  const progress = processingProgress(match);
  const readySlot = firstReadySlot(match);
  const hasVideo = matchHasReadyVideo(match);

  return (
    <article
      data-testid="match-card"
      data-match-id={match.id}
      className="group relative rounded border border-rule bg-surface hover:border-accent transition-colors overflow-hidden flex flex-col"
    >
      {match.has_thumbnail ? (
        <img
          src={`/api/matches/${encodeURIComponent(match.id)}/thumbnail`}
          alt=""
          loading="lazy"
          className="w-full aspect-video object-cover opacity-50 group-hover:opacity-70 transition-opacity"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
          data-testid="match-card-thumbnail"
        />
      ) : (
        <div className="w-full aspect-video bg-canvas" aria-hidden="true" />
      )}

      <div className="flex flex-col gap-2 p-3">
        <div className="flex items-center gap-2 text-xs font-mono text-ink-muted">
          {match.date ? <span>{match.date}</span> : null}
          {match.time ? <span>· {match.time}</span> : null}
          {match.location ? (
            <span className="truncate">· {match.location}</span>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3">
          <MatchTeamBadge name={match.home_team} logoUrl={homeUrl} />
          <div className="text-ink-muted text-xs font-mono">vs</div>
          <MatchTeamBadge name={match.away_team} logoUrl={awayUrl} />
        </div>

        {hasRecordedScore(match) ? (
          scoreHidden ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                reveal(match.id);
              }}
              data-testid="match-card-reveal-score"
              className="self-start rounded border border-rule bg-canvas px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:bg-field hover:text-ink"
            >
              Reveal score
            </button>
          ) : (
            <p
              data-testid="match-card-score"
              className="self-start text-sm font-mono text-ink"
            >
              {match.score_home} – {match.score_away}
            </p>
          )
        ) : (
          <p
            className="self-start text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
            data-testid="match-card-upcoming"
          >
            Upcoming
          </p>
        )}

        {progress !== null ? (
          <span
            data-testid="match-card-processing"
            className="self-start rounded border border-rule bg-canvas px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
          >
            Processing{progress > 0 ? ` ${progress}%` : ""}
          </span>
        ) : null}

        <div className="flex items-center justify-between gap-2 mt-1">
          {hasVideo && readySlot ? (
            <Link
              to="/match/$slug/$slot"
              params={{ slug: match.slug, slot: slotKeyToUrl(readySlot) }}
              // Carry the team slug as a `?from=` breadcrumb when this
              // grid is rendered on a team page, so GameShell's "Back
              // to Matches" link can return to that team's list.
              search={teamSlug ? { from: teamSlug } : undefined}
              data-testid="match-card-watch"
              className="text-xs text-accent hover:underline font-display uppercase tracking-wider"
            >
              View match →
            </Link>
          ) : (
            <Link
              to="/match/$slug"
              params={{ slug: match.slug }}
              search={teamSlug ? { from: teamSlug } : undefined}
              data-testid="match-card-open"
              className="text-xs text-ink-muted hover:text-ink hover:underline font-display uppercase tracking-wider"
            >
              Open →
            </Link>
          )}
          <div className="inline-flex items-center gap-2">
            {canEdit ? (
              <Link
                to="/admin/matches"
                data-testid="match-card-edit"
                className="text-[0.6rem] text-ink-muted hover:text-ink font-display uppercase tracking-wider"
              >
                Edit
              </Link>
            ) : null}
            {canDelete ? (
              <DeleteMatchAction match={match} teamSlug={teamSlug} />
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

function MatchTeamBadge({
  name,
  logoUrl,
}: {
  name: string;
  logoUrl: string | null;
}) {
  const initials = name
    .split(/\s+/)
    .map((s) => s.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");
  return (
    <div className="flex items-center gap-2 min-w-0 flex-1">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          loading="lazy"
          className="h-7 w-7 flex-shrink-0 rounded bg-canvas object-contain"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      ) : (
        <div
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded border border-rule bg-canvas text-[0.6rem] font-display text-ink-muted"
          aria-hidden="true"
        >
          {initials || "?"}
        </div>
      )}
      <span className="text-xs font-display uppercase tracking-wider text-ink truncate">
        {name}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-card delete (admin-only) — preserves the SeasonShell legacy parity.
// ---------------------------------------------------------------------------

function DeleteMatchAction({
  match,
  teamSlug,
}: {
  match: Match;
  teamSlug?: string;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch(`/api/matches/${encodeURIComponent(match.id)}`, {
        method: "DELETE",
        scope: false,
      });
      // Invalidate both shared caches (season view + admin matches), plus
      // the per-team-slug cache when the grid is rendered from a team page
      // so the row disappears from that surface without a manual refresh.
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("public-matches"),
      });
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-matches"),
      });
      if (teamSlug) {
        await queryClient.invalidateQueries({
          queryKey: unscopedKey("public-matches", { teamSlug }),
        });
      }
      setOpen(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          typeof err.detail === "string" ? err.detail : "Failed to delete match.",
        );
      } else {
        setError("Failed to delete match.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setError(null);
          setOpen(true);
        }}
        data-testid="match-card-delete"
        className="text-[0.6rem] text-ink-muted hover:text-destructive font-display uppercase tracking-wider"
      >
        Delete
      </button>
      {open ? (
        <ConfirmDialog
          title="Delete match?"
          message={
            <>
              <p>
                <strong>
                  {match.home_team || "—"} vs {match.away_team || "—"}
                </strong>
                {match.date ? ` (${match.date})` : ""} will be permanently
                removed along with all uploaded video, HLS variants, and
                coaching notes referencing it. This cannot be undone.
              </p>
              {error ? (
                <p
                  data-testid="match-card-delete-error"
                  className="mt-2 text-destructive font-mono text-xs"
                >
                  {error}
                </p>
              ) : null}
            </>
          }
          confirmLabel={submitting ? "Deleting…" : "Delete match"}
          danger
          onConfirm={onConfirm}
          onClose={() => {
            if (submitting) return;
            setOpen(false);
            setError(null);
          }}
          data-testid="match-card-delete-confirm"
        />
      ) : null}
    </>
  );
}
